import { useTranslation } from 'react-i18next';
import Constants from 'style/constants';
import MonitoringAlert from 'shared/components/monitoring/monitoring-alert/monitoring-alert';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';

import Gateway from 'features/gateway/domain/models/gateway';
import { GatewayStatus } from 'features/gateway/domain/models/gateway-status';

interface Props {
  gateway?: Gateway;
  includeServiceAlerts?: boolean;
}

const GatewayMonitoringAlert = ({ gateway, includeServiceAlerts = false }: Props) => {
  const { t } = useTranslation('monitoring');

  const getPrimaryAlert = (gateway?: Gateway) => {
    if (!gateway) return null;

    switch (gateway.status) {
      case GatewayStatus.offline:
        return (
          <MonitoringAlert
            key="gateway-offline"
            text={t('messages.gatewayOffline')}
            type={MonitoringType.GatewayOffline}
            color={Constants.Colors.error}
          />
        );
      case GatewayStatus.serviceOfflineOnly:
        return (
          <MonitoringAlert
            key="service-offline"
            text={t('messages.gatewayServiceOffline')}
            type={MonitoringType.GatewayServiceOffline}
            color={Constants.Colors.brightOrange}
          />
        );
      case GatewayStatus.passiveOfflineOnly: {
        const offlineChildGateway = gateway.childGateways?.find((item) => item.status === GatewayStatus.offline);
        if (offlineChildGateway) {
          const alertText = offlineChildGateway.isActiveInCluster
            ? t('messages.activeGatewayOfflineWithName', {
                gatewayName: offlineChildGateway.name,
              })
            : t('messages.passiveGatewayOfflineWithName', {
                gatewayName: offlineChildGateway.name,
              });

          return (
            <MonitoringAlert
              key="passive-offline"
              text={alertText}
              type={MonitoringType.PassiveGatewayOffline}
              color={Constants.Colors.brightOrange}
            />
          );
        }
        return null;
      }
      default:
        return null;
    }
  };

  if (includeServiceAlerts && gateway) {
    return (
      <>
        {getPrimaryAlert(gateway)}

        {gateway.status !== GatewayStatus.serviceOfflineOnly && gateway.gatewayServiceMonitorings?.some((service) => service.isOffline) && (
          <MonitoringAlert
            key="additional-service-offline"
            text={t('messages.gatewayServiceOffline')}
            type={MonitoringType.GatewayServiceOffline}
            color={Constants.Colors.brightOrange}
          />
        )}
      </>
    );
  }

  return getPrimaryAlert(gateway) || <></>;
};

export default GatewayMonitoringAlert;
